
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'
import { UserT } from '@/core/types/users.types'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { users } from '@/core/realisations/users.realisation'

// constants
import { BIDS_STATUSES } from '@/core/constants/bids.constants'
import { useRoute } from 'vue-router'
import { bids } from '@/core/realisations/bids.realisation'
import { BidStatus } from '@/core/types/bids.types'

const IS_ADDITTIONAL_LOAD_OPTIONS = [
    { id: '1', name: 'Да' },
    { id: '0', name: 'Нет' },
]

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isFtl: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['resetted', 'search'],
    setup(props, { emit }) {
        const route = useRoute()
        const id = route.query.id as string | undefined

        const state = reactive({
            id: null as number | null,
            route: null as string | null,
            carrierId: null as number | null,
            userId: null as number | null,
            dateOfCreation: null as string | null,
            statusId: null as number | null,
            isAboveCostStandard: null as number | null,
            isProject: null as number | null,
            isAdditionalLoad: null as string | null,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            statuses: new DataState<BidStatus>({}),
            users: new DataState<UserT>({}),
        })

        const reset = () => {
            if (props.loading) return

            state.id = null
            state.route = null
            state.carrierId = null
            state.userId = null
            state.dateOfCreation = null
            state.statusId = null
            state.isAboveCostStandard = null
            state.isProject = null
            state.isAdditionalLoad = null

            emit('resetted')
        }

        const search = () => {
            if (props.loading) return

            emit('search', {
                id: state.id,
                route: state.route,
                carrierId: state.carrierId,
                userId: state.userId,
                dateOfCreation: state.dateOfCreation,
                statusId: state.statusId,
                isAboveCostStandard: state.isAboveCostStandard,
                isProject: state.isProject,
                isAdditionalLoad: state.isAdditionalLoad,
            })
        }

        const loadStatuses = async () => {
            data_state.statuses.pagination.current_page = 1

            try {
                data_state.statuses.is_loading = true
                const response = await bids.getLogistBidStatuses()

                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                console.error(error)
                data_state.statuses.is_failed = true
            } finally {
                data_state.statuses.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2, isExtended: 0 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2, isExtended: 0 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // Создатели

        const loadUsers = async (page: number) => {
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const data = await users.getLogists(page, undefined, 0, 1)
                data_state.users.list = data.data

                data_state.users.pagination.total_pages = data.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const loadMoreUsers = async (page: number) => {
            if (page > data_state.users.pagination.total_pages) return
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const response = await users.getLogists(page, undefined, 0, 1)
                data_state.users.list = data_state.users.list.concat(response.data)

                data_state.users.pagination.total_pages = response.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        //

        onBeforeMount(() => {
            if (id) {
                state.id = Number(id)
            }
            loadStatuses()
            loadCarriers(1)
            loadUsers(1)
        })

        return {
            state,
            data_state,
            reset,
            search,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadUsers,
            loadMoreUsers,
            //
            loadStatuses,
            //
            BIDS_STATUSES,
            IS_ADDITTIONAL_LOAD_OPTIONS,
        }
    },
})
